import i18n from "@/i18n";
/**
 * CALENDAR-ABSENCES-V1 — one add / edit / cancel dialog for the canonical
 * Absence records, shared by Team, Schedule and Review Hours. Reviewed days
 * (open period) need a reason + explicit confirmation; closed periods explain
 * Reopen → edit → re-Close. Backend enforces every rule.
 */
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    AlertDialog, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatYMDDate } from "@/components/workforce/workforceHelpers";
import VuosilomaBookingFields from "@/components/workforce/VuosilomaBookingFields";
import {
    ABSENCE_TYPES, ABSENCE_TYPE_LABELS, REVIEWED_CODE, _MSG_CANCEL_BODY,
    _MSG_CANCEL_TITLE, _MSG_CLOSED_PATH, _MSG_NO_EMPLOYMENT, _MSG_PAID_LEAVE_HELPER,
    _MSG_SHIFT_WARNING, _MSG_TYPE_FACT_ONLY, _NOTE_HELPER, absenceErrorCode,
    absenceErrorMessage, absenceLabel, evidenceKindForAbsence, shiftDaysInRange,
} from "./absenceShared";

function ReviewedCorrectionFields({ reason, setReason, confirm, setConfirm }) {
    return (
        <div data-testid="absence-reviewed-correction"
            className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            <label className="block">Reason for the change (required)
                <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                    data-testid="absence-correction-reason" className="mt-1 bg-white" />
            </label>
            <label className="flex items-center gap-2">
                <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)}
                    data-testid="absence-correction-confirm" />
                {i18n.t("wf.absenceEditorDialog.iConfirmThisChangeToDates", "I confirm this change to dates with approved hours.")}
            </label>
        </div>
    );
}

export function AbsenceForm({
    initial, isEdit, onCancel, onSubmit, busy, employeeId, shiftDates, children, submitBlocked,
}) {
    const [absenceType, setAbsenceType] = useState(initial?.absence_type || ABSENCE_TYPES[0]);
    const [startDate, setStartDate] = useState(initial?.start_date || "");
    const [endDate, setEndDate] = useState(initial?.end_date || "");
    const [note, setNote] = useState(initial?.note || "");
    const [vl, setVl] = useState({
        funding_year: initial?.vuosiloma_funding_year || null,
        employer_confirmed: !!initial?.vuosiloma_employer_confirmed,
    });
    const isAnnual = absenceType === "ANNUAL_LEAVE";
    const invalid = !absenceType || !startDate || !endDate || (startDate > endDate);
    const shiftDays = shiftDaysInRange(shiftDates, startDate, endDate);
    return (
        <div className="space-y-3">
            <div>
                <label className="text-xs text-neutral-600">{i18n.t("common.type", "Type")}</label>
                <Select value={absenceType} onValueChange={setAbsenceType}>
                    <SelectTrigger data-testid="absence-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {ABSENCE_TYPES.map((t) => (
                            <SelectItem key={t} value={t} data-testid={`absence-form-type-${t}`}>
                                {ABSENCE_TYPE_LABELS[t]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-xs text-neutral-400 mt-1" data-testid="absence-form-type-helper">
                    {absenceType === "PAID_LEAVE" ? _MSG_PAID_LEAVE_HELPER : i18n.t("wf.absenceShared._MSG_TYPE_FACT_ONLY", _MSG_TYPE_FACT_ONLY)}
                </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-xs text-neutral-600">{i18n.t("wf.absenceEditorDialog.startDate", "Start date")}</label>
                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                        data-testid="absence-form-start" />
                </div>
                <div>
                    <label className="text-xs text-neutral-600">{i18n.t("wf.absenceEditorDialog.endDate", "End date")}</label>
                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                        data-testid="absence-form-end" />
                </div>
            </div>
            {shiftDays > 0 && (
                <p role="status" data-testid="absence-shift-warning"
                    className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    {_MSG_SHIFT_WARNING(shiftDays)}
                </p>
            )}
            {isAnnual && (
                <VuosilomaBookingFields employeeId={employeeId} startDate={startDate} endDate={endDate}
                    excludeAbsenceId={initial?.absence_id} value={vl} onChange={setVl} />
            )}
            <div>
                <label className="text-xs text-neutral-600">Operational note (optional)</label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder={i18n.t("wf.absenceEditorDialog.doNotIncludeMedicalDetails", "Do not include medical details.")} data-testid="absence-form-note" />
                <p className="text-xs text-neutral-400 mt-1" data-testid="absence-form-note-helper">
                    {i18n.t("wf.absenceShared._NOTE_HELPER", _NOTE_HELPER)}
                </p>
            </div>
            {children}
            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onCancel} disabled={busy}
                    data-testid="absence-form-cancel">{i18n.t("common.cancel", "Cancel")}</Button>
                <Button onClick={() => onSubmit({
                    absence_type: absenceType, start_date: startDate, end_date: endDate,
                    note: note || null,
                    ...(isAnnual ? {
                        vuosiloma_funding_year: vl.funding_year,
                        vuosiloma_employer_confirmed: vl.employer_confirmed,
                    } : {}),
                })} disabled={invalid || busy || submitBlocked} data-testid="absence-form-submit">
                    {isEdit ? i18n.t("common.save", "Save") : i18n.t("wf.absenceEditorDialog.addAbsence", "Add absence")}
                </Button>
            </div>
        </div>
    );
}

function EmployeePick({ employees, value, onChange }) {
    return (
        <div>
            <label className="text-xs text-neutral-600">{i18n.t("wf.absenceEditorDialog.employee", "Employee")}</label>
            <Select value={value || ""} onValueChange={onChange}>
                <SelectTrigger data-testid="absence-form-employee"><SelectValue placeholder={i18n.t("wf.absenceEditorDialog.choose", "Choose")} /></SelectTrigger>
                <SelectContent>
                    {employees.map((e) => (
                        <SelectItem key={e.employee_id} value={e.employee_id}
                            data-testid={`absence-form-employee-${e.employee_id}`}>
                            {`${e.first_name || ""} ${e.last_name || ""}`.trim() || e.employee_id}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

async function resolveEmploymentId(restaurantId, employeeId, asOf) {
    const { data } = await api.get(`/workforce/employees/${employeeId}`,
        { params: { restaurant_id: restaurantId, as_of: asOf } });
    return data?.employment?.employment_id || null;
}

function useAbsenceSave({ target, restaurantId, onSaved }) {
    const [busy, setBusy] = useState(false);
    const [needsReason, setNeedsReason] = useState(false);
    const [reason, setReason] = useState("");
    const [confirm, setConfirm] = useState(false);
    const [error, setError] = useState("");
    const correction = needsReason
        ? { correction_reason: reason.trim(), confirm_reviewed_correction: confirm } : {};
    const run = async (fn, okMsg) => {
        setBusy(true);
        setError("");
        try {
            await fn(correction);
            toast.success(okMsg);
            onSaved?.();
        } catch (err) {
            const code = absenceErrorCode(err);
            if (code === REVIEWED_CODE) setNeedsReason(true);
            const msg = err?.message === "NO_EMPLOYMENT" ? _MSG_NO_EMPLOYMENT : absenceErrorMessage(err);
            setError(code === "WORKFORCE_PERIOD_CLOSED" ? _MSG_CLOSED_PATH : msg);
            toast.error(msg);
        } finally { setBusy(false); }
    };
    const params = { restaurant_id: restaurantId };
    const create = (body, employeeId) => run(async (c) => {
        const employmentId = target.employmentId
            || await resolveEmploymentId(restaurantId, employeeId, body.start_date);
        if (!employmentId) throw new Error("NO_EMPLOYMENT");
        await api.post("/workforce/absences",
            { ...body, ...c, employee_id: employeeId, employment_id: employmentId }, { params });
    }, "Absence added");
    const update = (body) => run((c) => api.patch(
        `/workforce/absences/${target.absence.absence_id}`, { ...body, ...c }, { params }), "Absence updated");
    const cancel = () => run((c) => api.delete(
        `/workforce/absences/${target.absence.absence_id}`, { params: { ...params, ...c } }), "Absence cancelled");
    const blocked = needsReason && (!reason.trim() || !confirm);
    const fields = needsReason
        ? <ReviewedCorrectionFields reason={reason} setReason={setReason} confirm={confirm} setConfirm={setConfirm} />
        : null;
    return { busy, error, blocked, fields, create, update, cancel };
}

function ErrorNote({ error }) {
    if (!error) return null;
    return (
        <div role="alert" data-testid="absence-dialog-error"
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">{error}</div>
    );
}

function LockedBody({ target, onClose }) {
    return (
        <div className="space-y-3 text-sm">
            {target.absence && (
                <p data-testid="absence-locked-summary">
                    {absenceLabel(target.absence.absence_type)} · {formatYMDDate(target.absence.start_date)}
                    {" – "}{formatYMDDate(target.absence.end_date)}
                </p>
            )}
            <p data-testid="absence-closed-path"
                className="rounded-md border border-neutral-300 bg-neutral-50 px-3 py-2 text-xs text-neutral-700">
                {i18n.t("wf.absenceShared._MSG_CLOSED_PATH", _MSG_CLOSED_PATH)}
            </p>
            <div className="flex justify-end">
                <Button variant="outline" onClick={onClose} data-testid="absence-locked-close">{i18n.t("common.close", "Close")}</Button>
            </div>
        </div>
    );
}

function CancelBody({ save, onBack }) {
    return (
        <>
            <AlertDialogHeader>
                <AlertDialogTitle>{i18n.t("wf.absenceShared._MSG_CANCEL_TITLE", _MSG_CANCEL_TITLE)}</AlertDialogTitle>
                <AlertDialogDescription>{i18n.t("wf.absenceShared._MSG_CANCEL_BODY", _MSG_CANCEL_BODY)}</AlertDialogDescription>
            </AlertDialogHeader>
            {save.fields}
            <ErrorNote error={save.error} />
            <AlertDialogFooter>
                <Button variant="outline" onClick={onBack} disabled={save.busy}
                    data-testid="absence-cancel-keep">{i18n.t("wf.absenceEditorDialog.keep", "Keep")}</Button>
                <Button onClick={save.cancel} disabled={save.busy || save.blocked}
                    data-testid="absence-cancel-confirm">{i18n.t("wf.absenceEditorDialog.cancelAbsence", "Cancel absence")}</Button>
            </AlertDialogFooter>
        </>
    );
}

const TITLES = {
    get add() { return i18n.t("wf.absenceEditorDialog.addAbsence", "Add absence"); },
    get edit() { return i18n.t("wf.absenceEditorDialog.editAbsence", "Edit absence"); },
    get cancel() { return i18n.t("wf.absenceShared._MSG_CANCEL_TITLE", _MSG_CANCEL_TITLE); },
};
const TEST_IDS = { add: "absence-add-dialog", edit: "absence-edit-dialog", cancel: "absence-cancel-dialog" };

/**
 * target: { mode: "add"|"edit"|"cancel", employeeId?, employmentId?, absence?, date? }.
 * Mount with a fresh `key` per target so state resets.
 */
export default function AbsenceEditorDialog({
    target, restaurantId, employees, shiftDatesByEmployee, locked, onClose, onSaved, onOpenEvidence,
}) {
    const [mode, setMode] = useState(target.mode);
    const [employeeId, setEmployeeId] = useState(target.employeeId || "");
    const save = useAbsenceSave({ target, restaurantId, onSaved });
    const shiftDates = shiftDatesByEmployee?.[employeeId];
    const evidenceKind = target.absence ? evidenceKindForAbsence(target.absence.absence_type) : null;
    const initial = target.absence
        || (target.date ? { start_date: target.date, end_date: target.date } : null);
    return (
        <AlertDialog open onOpenChange={(v) => !v && onClose()}>
            <AlertDialogContent data-testid={TEST_IDS[mode]} className="max-h-[90vh] overflow-y-auto">
                {mode === "cancel" && !locked ? (
                    <CancelBody save={save} onBack={() => (target.mode === "cancel" ? onClose() : setMode("edit"))} />
                ) : (
                    <>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{TITLES[mode]}</AlertDialogTitle>
                            <AlertDialogDescription>{i18n.t("wf.absenceEditorDialog.recordAnOperationalAbsenceFact", "Record an operational absence fact.")}</AlertDialogDescription>
                        </AlertDialogHeader>
                        {locked ? <LockedBody target={target} onClose={onClose} /> : (
                            <>
                                {mode === "add" && !target.employeeId && employees && (
                                    <EmployeePick employees={employees} value={employeeId} onChange={setEmployeeId} />
                                )}
                                {mode === "edit" && (
                                    <div className="flex flex-wrap gap-2">
                                        <Button variant="outline" size="sm" onClick={() => setMode("cancel")}
                                            data-testid="absence-dialog-cancel-absence">{i18n.t("wf.absenceEditorDialog.cancelAbsence", "Cancel absence")}</Button>
                                        {evidenceKind && onOpenEvidence && (
                                            <Button variant="outline" size="sm"
                                                onClick={() => onOpenEvidence(target.absence, evidenceKind)}
                                                data-testid="absence-open-evidence">
                                                {i18n.t("wf.absenceEditorDialog.evidenceClassification", "Evidence classification")}
                                            </Button>
                                        )}
                                    </div>
                                )}
                                <AbsenceForm busy={save.busy} initial={initial} isEdit={mode === "edit"}
                                    employeeId={employeeId} shiftDates={shiftDates} onCancel={onClose}
                                    submitBlocked={save.blocked || (mode === "add" && !employeeId)}
                                    onSubmit={(body) => (mode === "edit"
                                        ? save.update(body) : save.create(body, employeeId))}>
                                    {save.fields}
                                    <ErrorNote error={save.error} />
                                </AbsenceForm>
                            </>
                        )}
                    </>
                )}
            </AlertDialogContent>
        </AlertDialog>
    );
}
